import React, { useState } from "react";
import { SEO } from "../../components/shared/SEO";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tab,
  Tabs,
  IconButton,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
} from "@mui/material";
import { CheckCircle, Cancel, Send } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  updateLeaveStatusAsync,
  addLeaveRequestAsync,
} from "../../store/slices/dataSlice";
import { showSnackbar } from "../../store/slices/uiSlice";
import { ROLES_CONFIG, LeaveRequest } from "../../types";
import Avatar from "../../components/shared/Avatar";
import StatusBadge from "../../components/shared/StatusBadge";
import StatCard from "../../components/shared/StatCard";

// Using global theme for table headers

const LeaveTable: React.FC<{
  requests: LeaveRequest[];
  canApprove: boolean;
  onAction: (id: string, status: string) => void;
}> = ({ requests, canApprove, onAction }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          {[
            "Employee",
            "Type",
            "Dates",
            "Days",
            "Reason",
            "Status",
            ...(canApprove ? ["Action"] : []),
          ].map((h) => (
            <TableCell key={h}>{h}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {requests.map((r) => (
          <TableRow key={r.id}>
            <TableCell
              sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar initials={r.initials} color={r.avColor} size={30} />
                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                  {r.name}
                </Typography>
              </Box>
            </TableCell>
            <TableCell
              sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
            >
              <Chip
                label={r.type}
                size="small"
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 600,
                  borderRadius: "99px",
                  background: "rgba(133,209,219,0.1)",
                  color: "#6BB8C3",
                  "& .MuiChip-label": { px: 1.2 },
                }}
              />
            </TableCell>
            <TableCell
              sx={{
                py: 1.5,
                px: 2,
                borderBottom: "1px solid #f1f5f9",
                fontSize: 12,
                fontFamily: "'JetBrains Mono',monospace",
              }}
            >
              {r.dates}
            </TableCell>
            <TableCell
              sx={{
                py: 1.5,
                px: 2,
                borderBottom: "1px solid #f1f5f9",
                fontSize: 13,
                fontFamily: "'JetBrains Mono',monospace",
                fontWeight: 600,
              }}
            >
              {r.days}d
            </TableCell>
            <TableCell
              sx={{
                py: 1.5,
                px: 2,
                borderBottom: "1px solid #f1f5f9",
                fontSize: 12,
                color: "#64748b",
                maxWidth: 180,
              }}
            >
              {r.reason}
            </TableCell>
            <TableCell
              sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
            >
              <StatusBadge status={r.status} />
            </TableCell>
            {canApprove && (
              <TableCell
                sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
              >
                {r.status === "Pending" ? (
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <IconButton
                      size="small"
                      sx={{ color: "#16a34a" }}
                      onClick={() => onAction(r.id, "Approved")}
                    >
                      <CheckCircle sx={{ fontSize: 20 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ color: "#dc2626" }}
                      onClick={() => onAction(r.id, "Rejected")}
                    >
                      <Cancel sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                    —
                  </Typography>
                )}
              </TableCell>
            )}
          </TableRow>
        ))}
        {requests.length === 0 && (
          <TableRow>
            <TableCell
              colSpan={7}
              sx={{
                textAlign: "center",
                py: 4,
                color: "#94a3b8",
                fontSize: 14,
              }}
            >
              No records found
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const Leave: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const leaveRequests = useAppSelector((s) => s.data.leaveRequests);
  const roleConfig = user
    ? ROLES_CONFIG[user.role.toLowerCase() as keyof typeof ROLES_CONFIG]
    : null;
  const canApprove = roleConfig?.canApproveLeave ?? false;

  const [tab, setTab] = useState(0);
  const [form, setForm] = useState({
    type: "Casual",
    startDate: "",
    endDate: "",
    reason: "",
  });

  const myLeaves = leaveRequests.filter(
    (r) => r.empId === user?.empId || (!r.empId && user?.role === "employee"),
  );
  const getUsedLeaves = (type: string) => {
    return myLeaves
      .filter((r) => r.status === "Approved" && r.type.includes(type))
      .reduce((sum, r) => sum + Number(r.days || 1), 0);
  };

  const casualUsed = getUsedLeaves("Casual");
  const sickUsed = getUsedLeaves("Sick");
  const annualUsed = getUsedLeaves("Annual");

  const totalCasual = 12;
  const totalSick = 12;
  const totalAnnual = 21;

  const handleAction = (id: string, status: string) => {
    dispatch(updateLeaveStatusAsync({ id, status }));
    dispatch(
      showSnackbar({
        message: `Leave ${status.toLowerCase()}`,
        severity: status === "Approved" ? "success" : "warning",
      }),
    );
  };

  const handleApply = () => {
    if (!form.startDate || !form.endDate || !form.reason) {
      dispatch(
        showSnackbar({ message: "Please fill all fields", severity: "error" }),
      );
      return;
    }

    if (form.reason.trim().length < 10) {
      dispatch(
        showSnackbar({
          message: "Reason must be at least 10 characters long",
          severity: "error",
        }),
      );
      return;
    }

    const start = new Date(form.startDate);
    const end = new Date(form.endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (start < today) {
      dispatch(
        showSnackbar({
          message: "Start date cannot be in the past",
          severity: "error",
        }),
      );
      return;
    }

    if (end < start) {
      dispatch(
        showSnackbar({
          message: "End date cannot be before start date",
          severity: "error",
        }),
      );
      return;
    }

    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

    let balance = Infinity;
    if (form.type === "Casual") balance = totalCasual - casualUsed;
    else if (form.type === "Sick") balance = totalSick - sickUsed;
    else if (form.type === "Annual") balance = totalAnnual - annualUsed;

    if (diffDays > balance && form.type !== "Unpaid") {
      dispatch(
        showSnackbar({
          message: `Insufficient ${form.type} leave balance. Available: ${balance} days`,
          severity: "error",
        }),
      );
      return;
    }

    const req: LeaveRequest = {
      id: `L${Date.now()}`,
      empId: user?.empId || "EMP123",
      name: user?.name || "Self",
      type: form.type,
      dates: `${form.startDate} – ${form.endDate}`,
      startDate: form.startDate,
      endDate: form.endDate,
      days: diffDays,
      status: "Pending",
      initials: user?.initials || "?",
      avColor: user?.avColor || "blue",
      reason: form.reason,
    };
    dispatch(addLeaveRequestAsync(req));
    dispatch(showSnackbar({ message: "Leave application submitted!" }));
    setForm({ type: "Casual", startDate: "", endDate: "", reason: "" });
  };

  const pending = leaveRequests.filter((r) => r.status === "Pending");
  const approved = leaveRequests.filter((r) => r.status === "Approved");
  const rejected = leaveRequests.filter((r) => r.status === "Rejected");
  const tabs = [
    { label: `All (${leaveRequests.length})`, data: leaveRequests },
    { label: `Pending (${pending.length})`, data: pending },
    { label: `Approved (${approved.length})`, data: approved },
  ];

  const totalBalance =
    totalCasual +
    totalSick +
    totalAnnual -
    (casualUsed + sickUsed + annualUsed);

  const dynamicLeaveStats = [
    {
      label: "Pending",
      value: pending.length.toString(),
      icon: "PendingActions",
      delta:
        pending.length > 0 ? `${pending.length} to review` : "All caught up",
      deltaType: (pending.length > 0 ? "down" : "neutral") as
        | "down"
        | "neutral"
        | "up",
      color: "#d97706",
      iconBg: "#fef3c7",
    },
    {
      label: "Approved",
      value: approved.length.toString(),
      icon: "CheckCircle",
      delta: "Overall",
      deltaType: "up" as const,
      color: "#16a34a",
      iconBg: "#dcfce7",
    },
    {
      label: "Rejected",
      value: rejected.length.toString(),
      icon: "Cancel",
      delta: "Overall",
      deltaType: "neutral" as const,
      color: "#dc2626",
      iconBg: "#fee2e2",
    },
    {
      label: "My Balance",
      value: totalBalance.toString(),
      icon: "EventAvailable",
      delta: "Days left",
      deltaType: "up" as const,
      color: "#85D1DB",
      iconBg: "rgba(133,209,219,0.1)",
    },
  ];

  const dynamicLeaveBalance = [
    {
      type: "Casual Leave",
      used: casualUsed,
      total: totalCasual,
      color: "#85D1DB",
    },
    { type: "Sick Leave", used: sickUsed, total: totalSick, color: "#dc2626" },
    {
      type: "Annual Leave",
      used: annualUsed,
      total: totalAnnual,
      color: "#16a34a",
    },
  ];

  return (
    <Box>
      <SEO title="Leave" noindex />
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {dynamicLeaveStats.map((s) => (
          <Grid item xs={12} sm={6} lg={3} key={s.label}>
            <StatCard {...s} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.5}>
        {/* Left */}
        <Grid item xs={12} md={4} lg={3}>
          <Card sx={{ mb: 2.5 }}>
            <CardContent>
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 2 }}>
                Leave Balance
              </Typography>
              {dynamicLeaveBalance.map((b) => (
                <Box key={b.type} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.6,
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                      {b.type}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#64748b",
                        fontFamily: "'JetBrains Mono',monospace",
                      }}
                    >
                      <span style={{ color: b.color, fontWeight: 700 }}>
                        {b.used}
                      </span>
                      /{b.total}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(b.used / b.total) * 100}
                    sx={{
                      height: 6,
                      borderRadius: 99,
                      backgroundColor: "#f1f5f9",
                      "& .MuiLinearProgress-bar": {
                        background: b.color,
                        borderRadius: 99,
                      },
                    }}
                  />
                </Box>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 2 }}>
                Apply for Leave
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel>Leave Type</InputLabel>
                <Select
                  value={form.type}
                  label="Leave Type"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, type: e.target.value }))
                  }
                >
                  {["Casual", "Sick", "Annual", "Unpaid"].map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Grid container spacing={1} sx={{ mb: 1.5 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Start Date"
                    type="date"
                    value={form.startDate}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, startDate: e.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="End Date"
                    type="date"
                    value={form.endDate}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, endDate: e.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
              <TextField
                fullWidth
                size="small"
                label="Reason"
                multiline
                rows={3}
                value={form.reason}
                onChange={(e) =>
                  setForm((f) => ({ ...f, reason: e.target.value }))
                }
                placeholder="Describe the reason..."
                sx={{ mb: 1.5 }}
              />
              <Button
                fullWidth
                variant="contained"
                startIcon={<Send />}
                onClick={handleApply}
                sx={{ borderRadius: 2 }}
              >
                Submit Application
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Right */}
        <Grid item xs={12} md={8} lg={9}>
          <Card>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{ borderBottom: "1px solid #e5e7eb", px: 1 }}
              TabIndicatorProps={{ style: { background: "#85D1DB" } }}
            >
              {tabs.map((t, i) => (
                <Tab
                  key={i}
                  label={t.label}
                  sx={{
                    fontSize: 13,
                    color: tab === i ? "#85D1DB" : "#64748b",
                  }}
                />
              ))}
            </Tabs>
            <LeaveTable
              requests={tabs[tab].data}
              canApprove={canApprove}
              onAction={handleAction}
            />
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Leave;
